/**
 * Helpers for SaaS Foundation organization member payloads
 * (GET /api/v1/organizations/current/members → MemberResponse[]).
 *
 * Lives in RcmPlatform/shared/frontend so Agentic CRA apps can depend on one module
 * via package.json: "@saas-foundation/shared-frontend": "file:../../shared/frontend"
 * (API implementation remains in SaaSFoundation backend / saas-frontend app).
 */

/** Sync with SaaSFoundation `kai_member.KAI_USER_EMAIL` / seeded org member role `ai_coworker` */
export const KAI_USER_EMAIL = 'kai@system.rcm.internal';
export const KAI_DISPLAY_NAME = 'Kai — AI Coworker';
export const KAI_MEMBER_ROLE = 'ai_coworker';

/**
 * True if this member is the seeded Kai user (check email on nested user).
 * @param {object} member MemberResponse-like
 */
export function isKaiOrganizationMember(member) {
  const email = (member?.user?.email || member?.email || '').toLowerCase();
  return email === KAI_USER_EMAIL;
}

/** Title-case a single local-part segment (e.g. david → David, HERNANDEZ2 → Hernandez2). */
function titleCaseSegment(segment) {
  if (!segment) return '';
  const m = segment.match(/^([a-zA-Z]+)(\d*)$/);
  if (m) {
    const letters = m[1];
    return letters.charAt(0).toUpperCase() + letters.slice(1).toLowerCase() + (m[2] || '');
  }
  return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
}

/** True if this dot segment looks like a role/suffix, not a person name (patricia.johnson.office_manager). */
function isLikelyRoleEmailSegment(seg) {
  if (!seg) return false;
  const x = seg.toLowerCase();
  if (['admin', 'owner', 'biller', 'supervisor', 'manager', 'user', 'staff', 'member'].includes(x)) {
    return true;
  }
  if (x.includes('_')) return true; // e.g. office_manager, frontend_admin, billing_supervisor
  if (x === 'frontend' || x === 'billing' || x === 'office') return true;
  return false;
}

/**
 * Best-effort "First Last" from email local part when API names are empty.
 * Strips trailing role-like segments, uses first two remaining parts.
 */
export function inferDisplayNameFromEmail(email) {
  if (!email || typeof email !== 'string') return '';
  const at = email.indexOf('@');
  if (at <= 0) return '';
  const local = email.slice(0, at);
  let parts = local.split('.').filter(Boolean);
  while (parts.length > 1 && isLikelyRoleEmailSegment(parts[parts.length - 1])) {
    parts.pop();
  }
  if (parts.length >= 2) {
    return `${titleCaseSegment(parts[0])} ${titleCaseSegment(parts[1])}`;
  }
  if (parts.length === 1) {
    return titleCaseSegment(parts[0]);
  }
  return '';
}

/**
 * Human-readable assignee label: "First Last (email@domain)" when possible.
 * @param {object} member - MemberResponse or legacy flat shape
 * @returns {string}
 */
export function formatMemberDisplayName(member) {
  if (!member) return '';
  const u = member.user || member;
  const email = (u.email || member.email || '').trim();
  const fn = (u.first_name || '').trim();
  const ln = (u.last_name || '').trim();
  const fromApi = `${fn} ${ln}`.trim();
  const fromMemberName = (member.name && String(member.name).trim()) || '';
  let namePart = fromApi || fromMemberName;
  if (!namePart && email) {
    namePart = inferDisplayNameFromEmail(email);
  }
  if (namePart && email) {
    return `${namePart} (${email})`;
  }
  if (email) return email;
  if (namePart) return namePart;
  return String(u.id || member.id || '');
}

/**
 * Stable user id for matching dropdown value / assignment id.
 * @param {object} member
 * @returns {string}
 */
export function getMemberAssigneeId(member) {
  if (!member) return '';
  return member.user?.id ?? member.user_id ?? member.id ?? '';
}

/**
 * Unique display names for round-robin demo assignment (Work Queue).
 * @param {object[]} members
 * @returns {string[]}
 */
export function membersToTeamDisplayNames(members) {
  if (!Array.isArray(members) || members.length === 0) return [];
  const seen = new Set();
  const out = [];
  for (const m of members) {
    const label = formatMemberDisplayName(m);
    if (label && !seen.has(label)) {
      seen.add(label);
      out.push(label);
    }
  }
  return out;
}

/** Kai / AI coworker labels (assignee column, not clinical "Dr." providers). */
export function isAiCoworkerAssigneeLabel(val) {
  if (val == null || val === '') return false;
  const s = String(val);
  const l = s.toLowerCase();
  return s.includes('Kai') || l.includes('ai coworker') || l === 'ai agent' || l === 'kai';
}

/**
 * Demo / placeholder human assignees to replace with org member names when available.
 */
export function shouldRemapDemoHumanAssignee(val) {
  if (val == null || val === '' || val === 'Unassigned') return false;
  if (isAiCoworkerAssigneeLabel(val)) return false;
  const s = String(val);
  if (s.startsWith('Dr.')) return true;
  if (/^User \d+$/i.test(s)) return true;
  return ['John Smith', 'Sarah Johnson', 'Michael Brown'].includes(s);
}

/**
 * Replace demo assignee strings on plain items (e.g. assigned_to, assignedTo).
 */
export function remapItemsAssigneeField(items, fieldName, teamMemberDisplayNames) {
  if (!Array.isArray(items) || !teamMemberDisplayNames?.length) return items;
  let idx = 0;
  return items.map((row) => {
    const val = row[fieldName];
    if (!shouldRemapDemoHumanAssignee(val)) return row;
    const name = teamMemberDisplayNames[idx % teamMemberDisplayNames.length];
    idx += 1;
    return { ...row, [fieldName]: name };
  });
}

/**
 * AgenticRCM queue rows use assigned_to_name + assigned_to_type + assigned_to id.
 */
export function remapAgenticRcmQueueHumanDisplayNames(items, teamMemberDisplayNames) {
  if (!Array.isArray(items) || !teamMemberDisplayNames?.length) return items;
  let idx = 0;
  return items.map((row) => {
    if (row.assigned_to_type === 'ai' || isAiCoworkerAssigneeLabel(row.assigned_to_name)) {
      return row;
    }
    const n = row.assigned_to_name || '';
    const id = row.assigned_to || '';
    const isDemoHuman =
      shouldRemapDemoHumanAssignee(n) || /^user-\d+$/i.test(String(id));
    if (!isDemoHuman) return row;
    const next = teamMemberDisplayNames[idx % teamMemberDisplayNames.length];
    idx += 1;
    return { ...row, assigned_to_name: next };
  });
}
